import * as React from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { useUpdateVenue, useCreateTrack, useUpdateTrack, useDeleteTrack } from '@/hooks/useVenues'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { requestErrorMessage } from '@/helpers/apiClient'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { FormError } from '@/components/ui/form-error'
import { useVenueMarketing } from '@/hooks/useVenueMarketing'
import type { VenueDto } from '@lib/dto/venue.dto'

/** What deleting a track does, said before it is done. */
function deleteTrackDescription(venue: VenueDto, trackId: string, hasPage: boolean): string {
  const fallback = venue.tracks.find((t) => t.id !== trackId)
  const rooms = venue.spaces.filter((s) => s.eventTrackId === trackId).map((s) => s.name)
  const parts: string[] = []
  if (rooms.length > 0 && fallback) {
    parts.push(`${rooms.join(', ')} ${rooms.length === 1 ? 'moves' : 'move'} to “${fallback.name}”.`)
  }
  if (hasPage) parts.push('Its public pricing page is deleted too, and anyone following a link to it will find nothing.')
  if (parts.length === 0) parts.push('It has no rooms, so nothing else changes.')
  return parts.join(' ')
}

/** One track's name row with save-on-blur and a delete control. */
function TrackRow({
  venue,
  trackId,
  name,
  canDelete,
  hasPage,
  onError,
}: {
  venue: VenueDto
  trackId: string
  name: string
  canDelete: boolean
  /** Whether the track has its own public pricing page, which deleting it takes. */
  hasPage: boolean
  /** Failures belong on screen — see the note on `VenueTracks`. */
  onError: (error: Error) => void
}): React.ReactElement {
  const venueId = venue.id
  const [value, setValue] = React.useState(name)
  const [confirming, setConfirming] = React.useState(false)
  const updateTrack = useUpdateTrack()
  const deleteTrack = useDeleteTrack()

  // A rename that lands, here or from someone else, moves the field with it.
  const [shownFor, setShownFor] = React.useState(name)
  if (shownFor !== name) {
    setShownFor(name)
    setValue(name)
  }

  /** A blank name, or one the server refused, goes back to the track's own: the field shows what is stored. */
  function save(): void {
    const trimmed = value.trim()
    if (trimmed === '') {
      setValue(name)
      return
    }
    if (trimmed === name) return
    updateTrack.mutate(
      { venueId, trackId, input: { name: trimmed } },
      {
        onError: (cause) => {
          setValue(name)
          onError(cause)
        },
      },
    )
  }

  return (
    <div className="flex items-center gap-2">
      <Input value={value} onChange={(e) => setValue(e.target.value)} onBlur={save} className="h-8" aria-label="Track name" />
      <Button
        variant="ghost"
        size="sm"
        disabled={!canDelete || deleteTrack.isPending}
        onClick={() => setConfirming(true)}
        aria-label="Delete track"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={`Delete “${name}”?`}
        description={deleteTrackDescription(venue, trackId, hasPage)}
        confirmLabel="Delete track"
        destructive
        busy={deleteTrack.isPending}
        onConfirm={() =>
          deleteTrack.mutate(
            { venueId, trackId },
            {
              onSuccess: () => setConfirming(false),
              onError: (e) => {
                setConfirming(false)
                onError(e)
              },
            },
          )
        }
      />
    </div>
  )
}

/** How the venue runs events, for someone who can see it but not change it. */
function VenueTracksReadOnly({ venue }: { venue: VenueDto }): React.ReactElement {
  if (!venue.supportsConcurrentEvents) {
    return <p className="text-sm text-ink-muted">One track — all spaces and stock are available to each event in turn.</p>
  }
  const trackName = new Map(venue.tracks.map((t) => [t.id, t.name]))
  return (
    <div className="space-y-3">
      <p className="text-sm text-ink-muted">Runs concurrent events, on these tracks:</p>
      <ul className="space-y-1 text-sm">
        {venue.tracks.map((t) => (
          <li key={t.id}>{t.name}</li>
        ))}
      </ul>
      <div className="space-y-1">
        <p className="text-xs font-medium text-ink-muted">Space assignment</p>
        {venue.spaces.map((s) => (
          <div key={s.id} className="flex items-center justify-between gap-2 text-sm">
            <span>{s.name}</span>
            <span className="text-ink-muted">
              {(s.eventTrackId === null ? undefined : trackName.get(s.eventTrackId)) ?? 'Unassigned'}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * Admin controls for a venue's event tracks: toggle whether it runs concurrent
 * events, manage the tracks, and assign each space to a track. A non-concurrent
 * venue keeps a single track owning every space.
 */
export function VenueTracks({
  venue,
  hasMarketing,
  canEdit,
}: {
  venue: VenueDto
  hasMarketing: boolean
  /** Edit adds turning concurrent events on and off, naming and deleting tracks, and moving spaces between them. */
  canEdit: boolean
}): React.ReactElement {
  // Read only to warn that deleting a track takes its pricing page, so only for someone who can delete one.
  const pages = useVenueMarketing(venue.id, hasMarketing && canEdit)
  const tracksWithPages = new Set((pages.data ?? []).flatMap((p) => (p.eventTrackId !== null ? [p.eventTrackId] : [])))
  const updateVenue = useUpdateVenue()
  const createTrack = useCreateTrack()
  const updateTrack = useUpdateTrack()
  const [error, setError] = React.useState<string | null>(null)

  const concurrent = venue.supportsConcurrentEvents

  /**
   * Every mutation on this panel reports here.
   *
   * They did not report anywhere, which is the whole of the bug this fixes: the
   * API refuses to move a space off the track of an event that has plans in it —
   * with a message that names the spaces — and the dropdown simply snapped back
   * to where it was. Nothing said no, so it read as a control that does nothing.
   */
  function fail(cause: Error): void {
    setError(requestErrorMessage(cause))
  }

  function assignSpace(spaceId: string, trackId: string): void {
    setError(null)
    const spaceIds = [...venue.spaces.filter((s) => s.eventTrackId === trackId).map((s) => s.id), spaceId]
    updateTrack.mutate({ venueId: venue.id, trackId, input: { spaceIds } }, { onError: fail })
  }

  if (!canEdit) return <VenueTracksReadOnly venue={venue} />

  return (
    <div>
      <label className="flex items-center gap-2 text-sm text-ink-muted">
        <input
          type="checkbox"
          checked={concurrent}
          onChange={() => {
            setError(null)
            updateVenue.mutate(
              { id: venue.id, input: { supportsConcurrentEvents: !concurrent } },
              { onError: fail },
            )
          }}
        />
        Runs concurrent events
      </label>

      {!concurrent ? (
        <p className="mt-2 text-sm text-ink-muted">
          One track — all spaces and stock are available to each event in turn. Enable concurrent events to split the
          venue into tracks that can be booked at the same time.
        </p>
      ) : (
        <div className="mt-3 space-y-3">
          <div className="space-y-2">
            {venue.tracks.map((t) => (
              <TrackRow
                key={t.id}
                venue={venue}
                trackId={t.id}
                name={t.name}
                canDelete={venue.tracks.length > 1}
                hasPage={tracksWithPages.has(t.id)}
                onError={fail}
              />
            ))}
            <Button
              variant="outline"
              size="sm"
              disabled={createTrack.isPending}
              onClick={() =>
                createTrack.mutate(
                  { venueId: venue.id, input: { name: `Track ${venue.tracks.length + 1}` } },
                  { onError: fail },
                )
              }
            >
              <Plus className="mr-1 h-3.5 w-3.5" />
              Add track
            </Button>
          </div>

          <div className="space-y-1">
            <p className="text-xs font-medium text-ink-muted">Space assignment</p>
            {/* Rooms first, then events: a room can't leave the track of an
                event planned in it, so sorting events first can lock rooms in. */}
            <p className="pb-1 text-xs text-ink-muted">
              Give each space its track before putting events on tracks. A space can’t move off the track of an event
              that has plans in it.
            </p>
            {venue.spaces.map((s) => (
              <div key={s.id} className="flex items-center justify-between gap-2 text-sm">
                <span>{s.name}</span>
                <Select
                  size="sm"
                  className="w-40"
                  value={s.eventTrackId ?? ''}
                  onChange={(e) => assignSpace(s.id, e.target.value)}
                  aria-label={`Track for ${s.name}`}
                >
                  {s.eventTrackId === null && <option value="">Unassigned</option>}
                  {venue.tracks.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </Select>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Outside both branches: turning concurrent events on fails while they are still off. */}
      {error ? <FormError className="pt-2">{error}</FormError> : null}
    </div>
  )
}
